/**
 * LWPOLYLINE entity → `RenderableEntity` adapter.
 *
 * Reads:
 *   - `Vertices` — `"x1,y1;x2,y2;…"` (matches `readPointList`)
 *   - `Bulges` — same shape, `"b1;b2;…"` (one bulge per *segment*; for
 *     a closed polyline the last bulge applies to the closing segment)
 *   - `Start widths` / `End widths` — DXF group codes 40 / 41 per
 *     vertex; absent on uniform-width polylines
 *   - `Closed` — `"true"` / `"false"`
 *
 * DEFUSE D3 — the geometry + bounds assembly now lives in the shared pure
 * builder `geom/lwpolylineGeometry.ts` (`buildLwpolylineSceneGeometry`), called
 * by BOTH this legacy bag adapter and the typed `typed/lwpolylineFromEntity.ts`
 * sibling. This file keeps ONLY its string-bag value extraction
 * (`readPointList` / `parseNumberList` / `parseWidthList` / the `Closed`
 * normalisation) and then delegates to the builder — behaviour-preserving.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readPointList } from "../../scene/renderHelpers";
import type { RenderableEntity, Vec3 } from "../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "./adapterContext";
import { buildLwpolylineSceneGeometry } from "./geom/lwpolylineGeometry";

export function lwpolylineToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  const points2D = readPointList(entity.properties.Vertices);
  const elevation = Number(entity.properties.Elevation ?? 0);
  const z = Number.isFinite(elevation) ? elevation : 0;
  const verticesRaw: Vec3[] = points2D.map((p) => ({ x: p.x, y: p.y, z }));
  // Codex P1-B — `buildLwpolylinePreview` now emits a `Bulges` bag (so the
  // property panel + grip seg-mid midpoint stay consistent with the arcs).
  // An all-zero bulge list is an arc-less polyline → drop it so the geometry
  // shape stays `{ kind, points, closed }`, matching the typed adapter's
  // all-zero filter (typed/lwpolylineFromEntity.ts) and the pre-emit baseline
  // (the preview carried no `Bulges` key at all before, so this read was []).
  const bulgesRaw = parseNumberList(entity.properties.Bulges);
  const bulges = bulgesRaw.some((b) => b !== 0) ? bulgesRaw : [];
  const closedRaw = String(entity.properties.Closed ?? "").trim().toLowerCase();
  // Accept canonical "true"/"false" plus the legacy "Yes"/"No" the
  // preview / import writers used to emit, so old serialized state and
  // any in-flight callers that haven't been migrated still parse.
  const closed = closedRaw === "true" || closedRaw === "yes" || closedRaw === "1";

  const widths = parseWidthList(
    entity.properties["Start widths"],
    entity.properties["End widths"],
  );

  const { geometry, bounds } = buildLwpolylineSceneGeometry(
    verticesRaw,
    closed,
    bulges,
    widths,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}

/** Parse `"a;b;c;…"` into a number array. */
function parseNumberList(raw: string | undefined): number[] {
  if (!raw) return [];
  return String(raw)
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
}

/**
 * Combine separate `Start widths` / `End widths` strings into a single
 * `{ start, end }[]` array, padding with `{0, 0}` when one side is
 * shorter or absent.
 */
function parseWidthList(
  startRaw: string | undefined,
  endRaw: string | undefined,
): { start: number; end: number }[] {
  const startList = parseNumberList(startRaw);
  const endList = parseNumberList(endRaw);
  const len = Math.max(startList.length, endList.length);
  if (len === 0) return [];
  const out: { start: number; end: number }[] = [];
  for (let i = 0; i < len; i++) {
    out.push({
      start: startList[i] ?? 0,
      end: endList[i] ?? 0,
    });
  }
  return out;
}
